import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getAuthToken } from "../../../utils/getAuthToken";
import axios from "axios";
import { getCached, setCached, invalidate, STORES, TTL } from "../../../utils/indexedDBCache";

const BASE_URL = `${process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000'}/api`;

// Module-level debounce so simultaneous mounts only fire one request.
const labelsFetchDebounceMap = new Map();
const LABELS_DEBOUNCE_TIME = 2000;
const shouldDebounceLabelsFetch = () => {
  const now = Date.now();
  const last = labelsFetchDebounceMap.get('fetchLabels');
  if (last && (now - last) < LABELS_DEBOUNCE_TIME) return true;
  labelsFetchDebounceMap.set('fetchLabels', now);
  return false;
};

// Clears the module-level debounce map — call on logout so the next
// account's first fetch is never silently skipped as "too soon".
export const __resetLabelsCaches = () => {
  labelsFetchDebounceMap.clear();
};

// Async thunk to fetch all labels
export const fetchLabels = createAsyncThunk(
  "labels/fetchLabels",
  async (_, { rejectWithValue }) => {
    if (shouldDebounceLabelsFetch()) return rejectWithValue("Debounced");

    // Serve from cache — labels rarely change.
    const cached = await getCached(STORES.LABELS, 'all');
    if (cached) return cached;

    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.get(`${BASE_URL}/label/get`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setCached(STORES.LABELS, 'all', response.data, TTL.LABELS);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.response?.data?.message || error.message);
    }
  }
);

// Async thunk to add a new label
export const addLabel = createAsyncThunk(
  "labels/addLabel",
  async (labelName, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/label/add`,
        { name: labelName },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // Server returns the updated label list — write it straight to cache.
      if (response.data) setCached(STORES.LABELS, 'all', response.data, TTL.LABELS);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.response?.data?.message || error.message);
    }
  }
);

// Async thunk to edit a label
export const editLabel = createAsyncThunk(
  "labels/editLabel",
  async ({ oldName, newName }, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.put(
        `${BASE_URL}/label/edit`,
        { oldName, newName },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data) setCached(STORES.LABELS, 'all', response.data, TTL.LABELS);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.response?.data?.message || error.message);
    }
  }
);

// Contacts that have a label (for delete confirmation)
export const fetchContactsWithLabel = createAsyncThunk(
  "labels/fetchContactsWithLabel",
  async (labelName, { rejectWithValue }) => {
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/label/contacts-with-label`,
        { name: labelName },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

// Async thunk to delete a label
export const deleteLabel = createAsyncThunk(
  "labels/deleteLabel",
  async (labelName, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/label/delete`,
        { name: labelName },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // Deleted label is gone from the list — invalidate so the next fetch is fresh.
      invalidate(STORES.LABELS, 'all');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.response?.data?.message || error.message);
    }
  }
);

// Async thunk to assign label to contact
export const assignLabelToContact = createAsyncThunk(
  "labels/assignLabelToContact",
  async ({ labelName, contactId }, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/label/assign`,
        { labelName, contactId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.response?.data?.message || error.message);
    }
  }
);

// Async thunk to create and assign label
export const createAndAssignLabel = createAsyncThunk(
  "labels/createAndAssignLabel",
  async ({ labelName, contactId }, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/label/create-and-assign`,
        { labelName, contactId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // New label was created — update cache if the response includes the new list.
      if (response.data?.labels) setCached(STORES.LABELS, 'all', response.data, TTL.LABELS);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.response?.data?.message || error.message);
    }
  }
);

// Async thunk to remove label from contact
export const removeLabelFromContact = createAsyncThunk(
  "labels/removeLabelFromContact",
  async ({ labelName, contactId }, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/label/remove`,
        { labelName, contactId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.response?.data?.message || error.message);
    }
  }
);

// Async thunk to remove all labels from contact
export const removeAllLabelsFromContact = createAsyncThunk(
  "labels/removeAllLabelsFromContact",
  async ({ contactId }, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/label/remove-all`,
        { contactId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.response?.data?.message || error.message);
    }
  }
);

// Async thunk for bulk label operations
export const bulkAssignLabels = createAsyncThunk(
  "labels/bulkAssignLabels",
  async ({ labelNames, contactIds }, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      // Process all assignments in parallel
      const promises = contactIds.flatMap(contactId =>
        labelNames.map(labelName =>
          axios.post(
            `${BASE_URL}/label/assign`,
            { labelName, contactId },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )
        )
      );

      const responses = await Promise.all(promises);
      return responses.map(response => response.data);
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.response?.data?.message || error.message);
    }
  }
);

export const bulkRemoveLabels = createAsyncThunk(
  "labels/bulkRemoveLabels",
  async ({ labelNames, contactIds }, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      // Process all removals in parallel
      const promises = contactIds.flatMap(contactId =>
        labelNames.map(labelName =>
          axios.post(
            `${BASE_URL}/label/remove`,
            { labelName, contactId },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )
        )
      );

      const responses = await Promise.all(promises);
      return responses.map(response => response.data);
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.response?.data?.message || error.message);
    }
  }
);

const labelsSlice = createSlice({
  name: "labels",
  initialState: {
    labels: [],
    isLoading: false,
    isError: false,
    errorMessage: null,
    labelFilter: [], // Array of selected labels for filtering
  },
  reducers: {
    clearErrors: (state) => {
      state.isError = false;
      state.errorMessage = null;
    },
    setLabelFilter: (state, action) => {
      state.labelFilter = action.payload;
    },
    addToLabelFilter: (state, action) => {
      if (!state.labelFilter.includes(action.payload)) {
        state.labelFilter.push(action.payload);
      }
    },
    removeFromLabelFilter: (state, action) => {
      state.labelFilter = state.labelFilter.filter(label => label !== action.payload);
    },
    clearLabelFilter: (state) => {
      state.labelFilter = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Labels
      .addCase(fetchLabels.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(fetchLabels.fulfilled, (state, action) => {
        state.isLoading = false;
        state.labels = action.payload.labels || [];
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(fetchLabels.rejected, (state, action) => {
        state.isLoading = false;
        if (action.payload !== "Debounced") {
          state.isError = true;
          state.errorMessage = action.payload;
        }
      })

      // Add Label
      .addCase(addLabel.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(addLabel.fulfilled, (state, action) => {
        state.isLoading = false;
        state.labels = action.payload.labels || [];
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(addLabel.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })

      // Edit Label
      .addCase(editLabel.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(editLabel.fulfilled, (state, action) => {
        state.isLoading = false;
        state.labels = action.payload.labels || [];
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(editLabel.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })

      // Delete Label
      .addCase(deleteLabel.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(deleteLabel.fulfilled, (state, action) => {
        state.isLoading = false;
        state.labels = action.payload.labels || [];
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(deleteLabel.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })

      // Assign Label to Contact
      .addCase(assignLabelToContact.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(assignLabelToContact.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(assignLabelToContact.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })

      // Create and Assign Label
      .addCase(createAndAssignLabel.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(createAndAssignLabel.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.labels) {
          state.labels = action.payload.labels;
        }
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(createAndAssignLabel.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })

      // Remove Label from Contact
      .addCase(removeLabelFromContact.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(removeLabelFromContact.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(removeLabelFromContact.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })

      // Remove All Labels from Contact
      .addCase(removeAllLabelsFromContact.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(removeAllLabelsFromContact.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(removeAllLabelsFromContact.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })

      // Bulk Assign Labels
      .addCase(bulkAssignLabels.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(bulkAssignLabels.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(bulkAssignLabels.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })

      // Bulk Remove Labels
      .addCase(bulkRemoveLabels.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(bulkRemoveLabels.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(bulkRemoveLabels.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      });
  },
});

export const {
  clearErrors,
  setLabelFilter,
  addToLabelFilter,
  removeFromLabelFilter,
  clearLabelFilter,
} = labelsSlice.actions;

export default labelsSlice.reducer;
